import cn from 'classnames';
import React, { type ReactNode, useEffect } from 'react';
import { useAppContext } from 'src/context/store/app-provider';
import { useTogglerState } from 'src/hooks/use-toggler-state';

import { ShadowView } from '../shadow-view';
import { useOnEscape } from './hooks/use-on-escape';
import styles from './styles.css';

type Props = {
  isShow: boolean;
  children: ReactNode;
  className?: string;
  classNameContent?: string;
  onClose: () => void;
  onCloseAfter?: () => void;
};

export const Dialog = ({
  isShow,
  className,
  classNameContent,
  children,
  onClose,
  onCloseAfter,
}: Props) => {
  const { isRtl } = useAppContext();

  const [isShowPopup, { switchOn: showPopup, switchOff: hidePopup }] =
    useTogglerState();

  useOnEscape(onClose);

  useEffect(() => {
    if (isShow) {
      showPopup();
    }
  }, [isShow, showPopup]);

  if (!isShowPopup) return null;

  const closeDialog = ({ target, currentTarget }) => {
    if (target === currentTarget) {
      onClose();
    }
  };

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (e.animationName === styles.slideDown) {
      hidePopup();
      onCloseAfter?.();
    }
  };

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'}>
      <div className={cn(styles.dialog, className)} onClick={closeDialog}>
        <div
          className={cn(styles.dialogContent, classNameContent, {
            [styles.disappearing]: !isShow,
          })}
          onAnimationEnd={handleAnimationEnd}
        >
          <ShadowView shouldInheritColor>
            <span dir={isRtl ? 'rtl' : 'ltr'}>{children}</span>
          </ShadowView>
        </div>
      </div>
    </div>
  );
};
